// @ts-strict-ignore
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef } from "react";

import type * as monacoEditor from "monaco-editor/esm/vs/editor/editor.api";

import type { TranslationMatch } from "@lib/findTranslationMatches/findTranslationMatches";
import { findTranslationMatches } from "@lib/findTranslationMatches/findTranslationMatches";
import { useClassElements } from "@lib/useClassElements";

import type { Monaco } from "@components/MonacoHtmlEditor/config/monacoLoader";

import type { DecorationFn, UseDecorateTranslationTagsOptions } from "./types";

const translationHighlightClass = "translation-highlight";

// Function to find all matches of your pattern
const findMatches = (
  content: string,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monaco: Monaco
): TranslationMatch[] => {
  const model = editor.getModel();
  if (!model) {
    return [];
  }

  return findTranslationMatches(content, (match) => {
    const startPosition = model.getPositionAt(match.index);
    const endPosition = model.getPositionAt(match.index + match[0].length);

    return {
      range: new monaco.Range(
        startPosition.lineNumber,
        startPosition.column,
        endPosition.lineNumber,
        endPosition.column
      ),
      text: match[0],
    };
  });
};

// Apply decorations with blue background
const applyDecorations = (
  decorations: MutableRefObject<string[]>,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  monaco: Monaco
): string[] => {
  const model = editor.getModel();
  if (!model) {
    return;
  }

  const content = model.getValue();
  const matches = findMatches(content, editor, monaco);

  const popoverTargetClassNames: string[] = [];

  if (matches.length === 0) {
    // Clear all decorations if no matches
    decorations.current = editor.deltaDecorations(decorations.current, []);
    return popoverTargetClassNames;
  }

  // Create decorations for each match
  const allDecorations = matches.flatMap((match) => {
    // Unique class name for each match
    const setClassName = `${translationHighlightClass}-${match.range.startLineNumber}-${match.range.startColumn}--${match.range.endLineNumber}-${match.range.endColumn}`;

    // Add the unique class name to the list of popover targets
    popoverTargetClassNames.push(setClassName);

    // Basic highlight for the whole match
    const basicHighlight: monacoEditor.editor.IModelDeltaDecoration = {
      range: match.range,
      options: {
        inlineClassName: `${translationHighlightClass} ${setClassName}`,
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    };

    // Create a range for just the end of the match (last character)
    const endRange = new monaco.Range(
      match.range.endLineNumber,
      match.range.endColumn - 1,
      match.range.endLineNumber,
      match.range.endColumn
    );

    // Hover decoration for the end with popover and "button"
    const endHighlight: monacoEditor.editor.IModelDeltaDecoration = {
      range: endRange,
      options: {
        inlineClassName: `${translationHighlightClass}-end`,
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    };

    return [basicHighlight, endHighlight];
  });

  // Apply all decorations
  decorations.current = editor.deltaDecorations(decorations.current, allDecorations);

  return popoverTargetClassNames;
};

/**
 * Async version that properly checks if an element is hidden by overflow
 * @param element The DOM element to check
 * @returns Promise resolving to whether the element is hidden
 */
const isElementHiddenByOverflowAsync = (element: HTMLElement | null): Promise<boolean> => {
  if (!element) {
    return Promise.resolve(true);
  }

  const elementRect: DOMRect = element.getBoundingClientRect();

  // If element has no size, it's not visible
  if (elementRect.width === 0 || elementRect.height === 0) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isHidden = entry.intersectionRatio === 0;
        observer.disconnect();
        resolve(isHidden);
      },
      {
        root: null, // Check visibility within viewport
        threshold: 0, // Trigger as soon as even 1px is visible
      }
    );

    observer.observe(element);
  });
};

/**
 * Custom hook to decorate translation tags in Monaco editor
 *
 * @param options Options for the hook
 * @returns A tuple containing the decoration function, state for the popover, and a ref for the popover target
 */
export const useDecorateTranslationTags = ({
  containerRef,
  delay = 200,
  enabled = false,
}: UseDecorateTranslationTagsOptions): DecorationFn => {
  // Reference to store the Monaco decorations
  const decorations = useRef<string[]>([]);
  // Reference to store the class names of the popover targets
  const popoverTargetClassNames = useRef<string[]>([]);
  // Reference to store the target element to append popover to
  const targetRef = useRef<HTMLElement | null>(null);
  // Reference to store the timeout ID
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Reference to store the last element that triggered mouseLeave
  const lastLeaveElementRef = useRef<Element | null>(null);

  const elements = useClassElements(translationHighlightClass);

  const handleMouseEnter = useCallback(
    async (event: MouseEvent) => {
      // 1. Identify which of the popover target class names the mouse is entering
      const target = event.currentTarget as HTMLElement;
      const matchingClassName = Array.from(target.classList).find((className) =>
        popoverTargetClassNames.current.includes(className)
      );

      if (!matchingClassName) {
        return;
      }

      // If there's a pending mouseLeave timeout
      if (timeoutRef.current !== null && lastLeaveElementRef.current) {
        // Cancel the pending mouseLeave since we're entering another translation highlight
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        lastLeaveElementRef.current = null;
      }

      // 2a. Identify the top-level element that contains the popover target
      const container = containerRef?.current || document;

      // 2b. Get the last element with the class name to identify popover target
      const lastElement = Array.from(container.getElementsByClassName(matchingClassName)).pop() as HTMLElement;

      // 3. Check if the element is hidden by overflow, if so close any open popover
      const isHidden = await isElementHiddenByOverflowAsync(lastElement);

      if (isHidden) {
        // Close popover if the element is hidden
        targetRef.current = null;
        return;
      }

      // 4. Update targetRef to the current element (for repositioning)
      targetRef.current = lastElement;
    },
    [containerRef]
  );

  const handleMouseLeave = useCallback(
    (event: MouseEvent) => {
      // Store the element that triggered mouseLeave
      lastLeaveElementRef.current = event.currentTarget as HTMLElement;

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set a new timeout for handleMouseLeave
      timeoutRef.current = setTimeout(async () => {
        // 1. Get the related target (i.e. where the mouse is going)
        const relatedTarget = event.relatedTarget as HTMLElement | null;

        // 2. If the relatedTarget is not defined, close the popover
        if (!relatedTarget) {
          targetRef.current = null;
          return;
        }

        // 3. Check if the mouse is moving to another translation highlight
        const isMovingToAnotherTranslation = Array.from(relatedTarget.classList || []).some((className) => {
          // Check if the class indicates it's a translation highlight
          return (
            popoverTargetClassNames.current.includes(className) ||
            className === translationHighlightClass ||
            className.startsWith(`${translationHighlightClass}-`)
          );
        });

        // If not moving to another translation highlight, close the popover
        if (!isMovingToAnotherTranslation) {
          targetRef.current = null;
          timeoutRef.current = null;
          lastLeaveElementRef.current = null;
          return;
        }

        // 4. If we are moving to another translation, check if it's hidden by overflow
        // Find the matching class name for the new translation element
        const newMatchingClassName = Array.from(relatedTarget.classList).find((className) =>
          popoverTargetClassNames.current.includes(className)
        );

        if (newMatchingClassName) {
          const container = containerRef?.current || document;
          const newElement = Array.from(
            container.getElementsByClassName(newMatchingClassName)
          ).pop() as HTMLElement;

          // Check if the new element is hidden
          const isNewElementHidden = await isElementHiddenByOverflowAsync(newElement);

          if (isNewElementHidden) {
            targetRef.current = null;
          } else {
            // Update target reference for smooth transition
            targetRef.current = newElement;
          }
        }

        // Clear the timeout reference
        timeoutRef.current = null;
        lastLeaveElementRef.current = null;
      }, delay);
    },
    [delay, containerRef]
  );

  const decorateTranslationTags = useCallback(
    ({ editor, monaco }: { editor: monacoEditor.editor.IStandaloneCodeEditor; monaco: Monaco }) => {
      if (!enabled) {
        return;
      }

      const model = editor.getModel();

      if (!model) {
        return;
      }

      // Apply decorations on initial render
      popoverTargetClassNames.current = applyDecorations(decorations, editor, monaco);

      // Apply decorations on content change
      model.onDidChangeContent(() => {
        popoverTargetClassNames.current = applyDecorations(decorations, editor, monaco);
      });
    },
    [enabled]
  );

  useEffect(() => {
    if (elements.length === 0) {
      return;
    }

    elements.forEach((element) => {
      element.addEventListener("mouseenter", handleMouseEnter);
      element.addEventListener("mouseleave", handleMouseLeave);
    });

    return () => {
      elements.forEach((element) => {
        element.removeEventListener("mouseenter", handleMouseEnter);
        element.removeEventListener("mouseleave", handleMouseLeave);
      });
    };
  }, [elements, handleMouseEnter, handleMouseLeave]);

  return decorateTranslationTags;
};
